import { useEffect, useState } from "react";
import "@/App.css";
import { Toaster } from "sonner";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Instagram, Facebook } from "lucide-react";
import Admin from "@/Admin";

const SOCIAL_LINKS = {
  instagram: "https://www.instagram.com/gingercircle_fr",
  facebook: "https://www.facebook.com/people/Ginger-Circle/61594318145114/",
};

/* ─── Sections (V2) ─────────────────────────────────────── */

function Nav({ onJoin }) {
  return (
    <nav className="nav" data-testid="top-nav">
      <div className="container nav-inner">
        <span className="wordmark">Ginger<span className="dot">.</span></span>
        <div className="nav-right">
          <div className="nav-socials" data-testid="nav-socials">
            <a
              href={SOCIAL_LINKS.instagram}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram Ginger Circle"
              className="social-icon social-icon-nav"
              data-testid="nav-social-instagram"
            >
              <Instagram size={18} strokeWidth={1.6} aria-hidden="true" />
            </a>
            <a
              href={SOCIAL_LINKS.facebook}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook Ginger Circle"
              className="social-icon social-icon-nav"
              data-testid="nav-social-facebook"
            >
              <Facebook size={18} strokeWidth={1.6} aria-hidden="true" />
            </a>
          </div>
          <a href="tel:+33662188624" className="nav-phone" data-testid="nav-phone">
            <span className="phone-icon" aria-hidden="true">📞</span>
            <span className="phone-number">06 62 18 86 24</span>
          </a>
          <button onClick={onJoin} className="nav-cta" data-testid="nav-cta">Inscrivez-vous</button>
        </div>
      </div>
    </nav>
  );
}

function SharpLaunch({ onJoin }) {
  return (
    <section className="hero launch" data-testid="sharp-launch">
      <div className="container">
        <div className="hero-immersive" data-testid="hero-immersive">
          <img
            src={`${process.env.PUBLIC_URL}/hero-immersive.jpg`}
            alt="Cours Ginger Circle — muscler le corps et le cerveau"
            loading="eager"
          />
          <div className="hero-immersive-overlay" aria-hidden="true" />
          <span className="hero-immersive-badge">Nouveau à Tours</span>
          <h1 className="hero-immersive-title">
            Un cours qui muscle <span className="accent">le corps</span> et <span className="accent">le cerveau</span>.
          </h1>
        </div>

        <div className="hero-meta">
          <span className="hero-meta-pill">Cours hebdomadaire</span>
          <span className="hero-meta-pill">Ouvert à tout âge et tout niveau</span>
          <span className="hero-meta-pill">1er cours d'essai gratuit</span>
        </div>

        <div className="two-formats-band" data-testid="two-formats-band">
          <span className="eyebrow">Deux formats complémentaires</span>
          <div className="two-formats-grid">
            <div className="two-formats-cell">
              <strong>Ginger Deep</strong> — renforcement des muscles profonds associé à un travail de mémoire et de concentration.
            </div>
            <div className="two-formats-cell">
              <strong>Ginger Sharp</strong> — cardio varié et stimulant, avec exercices de réflexes et de vivacité.
            </div>
          </div>
        </div>

        <div className="sessions">
          <div className="session-card session-deep" data-testid="session-deep-mardi">
            <span className="session-format">Ginger Deep</span>
            <span className="session-day">Chaque mardi</span>
            <span className="session-time">14h30</span>
            <span className="session-venue">La Belle Étoile<br />9 allée de la Fauvette</span>
          </div>
          <div className="session-card session-sharp" data-testid="session-sharp-jeudi">
            <span className="session-format">Ginger Sharp</span>
            <span className="session-day">Chaque jeudi matin</span>
            <span className="session-time">Horaire à confirmer : 9h, 10h ou 11h</span>
            <span className="session-venue">Le Petit Morier<br />81 boulevard Jean Royer, 37000 Tours</span>
          </div>
          <div className="session-card session-sharp" data-testid="session-sharp-vendredi">
            <span className="session-format">Ginger Sharp</span>
            <span className="session-day">Chaque vendredi</span>
            <span className="session-time">14h</span>
            <span className="session-venue">Colette Gym<br />54 rue Sébastopol, Tours</span>
          </div>
        </div>

        <div className="hero-cta-row">
          <button onClick={onJoin} className="cta-primary cta-big" data-testid="sharp-cta">
            Inscrivez-vous <span className="arrow">→</span>
          </button>
          <span className="places-note">Places limitées — premier arrivé, premier servi.</span>
        </div>
      </div>
    </section>
  );
}

function Places() {
  return (
    <section className="places-section" data-testid="places-section">
      <div className="container">
        <span className="eyebrow">Nos lieux de cours à Tours</span>
        <div className="places-map" data-testid="places-map">
          <img
            src={`${process.env.PUBLIC_URL}/carte_3_lieux.png`}
            alt="Carte des lieux de cours à Tours : La Belle Étoile, Colette Gym, Le Petit Morier"
            loading="lazy"
          />
        </div>
        <div className="places-map-legend">
          <span className="places-map-legend-item">
            <span className="dot dot-teal" aria-hidden="true" />
            La Belle Étoile — 9 allée de la Fauvette, Tours
          </span>
          <span className="places-map-legend-item">
            <span className="dot dot-honey" aria-hidden="true" />
            Colette Gym — 54 rue Sébastopol, Tours
          </span>
          <span className="places-map-legend-item">
            <span className="dot dot-honey" aria-hidden="true" />
            Le Petit Morier — 81 boulevard Jean Royer, 37000 Tours
          </span>
        </div>
      </div>
    </section>
  );
}

function Verbatims({ onJoin }) {
  const items = [
    { quote: "J'ai découvert qu'on peut s'entraîner à trouver ses mots plus vite… en gardant la pêche !", author: "Stéphane" },
    { quote: "C'est fou ce qu'1 heure ça passe vite, j'ai le cerveau qui pétille !", author: "Fati" },
    { quote: "On repart avec les jambes qui tirent un peu et la tête bien claire, c'est un drôle de mélange que j'adore.", author: "Marie" },
  ];
  return (
    <section className="verbatims-section" data-testid="verbatims-section">
      <div className="container">
        <h2 className="verbatims-h2">
          On transpire, on rigole… et on muscle son cerveau&nbsp;!
        </h2>
        <div className="verbatims-grid">
          {items.map((v) => (
            <div className="verbatim-card" key={v.author}>
              <p className="verbatim-quote">« {v.quote} »</p>
              <p className="verbatim-author">— {v.author}</p>
            </div>
          ))}
        </div>
        <div className="section-cta-center">
          <button onClick={onJoin} className="cta-primary cta-big" data-testid="verbatims-cta">
            Inscrivez-vous <span className="arrow">→</span>
          </button>
        </div>
      </div>
    </section>
  );
}

function PressNouvelleRepublique({ articleUrl }) {
  const logo = (
    <img
      src={`${process.env.PUBLIC_URL}/nouvelle-republique.png`}
      alt="la Nouvelle République"
      className="press-logo-img"
      width="240"
      loading="lazy"
    />
  );
  return (
    <section className="press-section" data-testid="press-section">
      <div className="container">
        <div className="press-rule" aria-hidden="true" />
        <div className="press-inner">
          <div className="press-logo-wrap">
            {articleUrl ? (
              <a
                href={articleUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Lire l'article de la Nouvelle République"
                className="press-logo-link"
                data-testid="press-logo-link"
              >
                {logo}
              </a>
            ) : (
              logo
            )}
          </div>
          <div className="press-text">
            <span className="press-eyebrow">Ils parlent de nous</span>
            <p className="press-quote">
              « Venez faire transpirer vos muscles et votre cerveau »
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function ApprocheUnique() {
  return (
    <section className="approche-section" data-testid="approche-section">
      <div className="container">
        <h2 className="approche-h2">Une approche unique</h2>
        <div className="approche-body">
          <p>
            Quand le corps fait un effort, il libère du BDNF, une substance qui ouvre
            une fenêtre de plasticité dans le cerveau. En sollicitant l'esprit au même
            moment, on en démultiplie les bénéfices.
          </p>
          <p>
            Au contact de neurologues et de coachs sportifs, nous avons développé une
            approche qui muscle, en mouvement, 4&nbsp;compétences bien réelles&nbsp;:
            la mémoire, l'attention, les réflexes et la facilité à trouver ses mots.
          </p>
        </div>
      </div>
    </section>
  );
}

function Tarifs({ onJoin }) {
  return (
    <section className="tarifs-section" data-testid="tarifs-section">
      <div className="container">
        <span className="eyebrow">Tarifs</span>
        <h2 className="tarifs-h2">
          Un tarif <span className="accent">dégressif</span>.
        </h2>
        <div className="tarifs-grid">
          <div className="tarif-card">
            <span className="tarif-label">À l'unité</span>
            <div className="tarif-price">
              18&nbsp;€<span className="tarif-per">&nbsp;/&nbsp;h</span>
            </div>
            <span className="tarif-note">Une séance, payée avant le cours.</span>
          </div>
          <div className="tarif-card">
            <span className="tarif-label">Carnet 5 séances</span>
            <div className="tarif-price">
              16&nbsp;€<span className="tarif-per">&nbsp;/&nbsp;h</span>
            </div>
            <span className="tarif-note">Soit 80&nbsp;€ le carnet.</span>
          </div>
          <div className="tarif-card tarif-best">
            <span className="tarif-label">Carnet 10 séances</span>
            <div className="tarif-price">
              15&nbsp;€<span className="tarif-per">&nbsp;/&nbsp;h</span>
            </div>
            <span className="tarif-note">Soit 150&nbsp;€ le carnet — le meilleur tarif.</span>
          </div>
        </div>
        <div className="section-cta-center">
          <button onClick={onJoin} className="cta-primary cta-big" data-testid="tarifs-cta">
            Inscrivez-vous <span className="arrow">→</span>
          </button>
        </div>
      </div>
    </section>
  );
}

function Methode({ onJoin }) {
  const items = [
    {
      num: "01",
      title: "Le double travail, concept fondateur",
      body: "Corps et cerveau au même instant, dans la même séance. Une méthode pensée comme un tout, pas comme la somme de deux disciplines.",
    },
    {
      num: "02",
      title: "Des événements festifs et ludiques pour la motivation",
      body: "Le plaisir n'est pas une option, c'est le moteur de la régularité. Cours, défis collectifs, événements Ginger Games — la motivation tient parce qu'on s'amuse et qu'on progresse, ensemble.",
    },
    {
      num: "03",
      title: "Un renouvellement régulier pour progresser",
      body: "Nouveau contenu chaque trimestre. R&D continue. Jamais le même cours deux fois — pour que le cerveau apprenne, et que le corps suive.",
    },
  ];
  return (
    <section className="methode-section" data-testid="methode-section">
      <div className="container">
        <span className="eyebrow">La méthode</span>
        <h2 className="methode-h2">
          Le corps et le cerveau, <span className="accent">en même temps</span>.
        </h2>
        <p className="methode-lead">
          Ginger Circle a fait du double travail le concept fondateur de la méthode.
          Chaque exercice sollicite le corps et le cerveau au même moment, en cours collectif.
        </p>
        <div className="methode-grid">
          {items.map((it) => (
            <div className="methode-item" key={it.num}>
              <div className="methode-item-rule" aria-hidden="true" />
              <span className="methode-num">{it.num}</span>
              <h3>{it.title}</h3>
              <p>{it.body}</p>
            </div>
          ))}
        </div>
        <div className="section-cta-center">
          <button onClick={onJoin} className="cta-primary cta-big" data-testid="methode-cta">
            Inscrivez-vous <span className="arrow">→</span>
          </button>
        </div>
      </div>
    </section>
  );
}

function Etudes({ onJoin }) {
  const studies = [
    {
      stat: "+83\u00A0%",
      label: "de fonctions exécutives chez les 60-77 ans.",
      desc: (
        <>
          Bouger et stimuler le cerveau en même temps fait progresser les fonctions
          exécutives de 83&nbsp;% chez les 60-77 ans, selon l'étude FINGER
          (<em>The Lancet</em>, 2015, n&nbsp;=&nbsp;1&nbsp;260, 2&nbsp;ans).
        </>
      ),
      ref: (<>Ngandu T. et al. · <em>The Lancet</em> 385(9984) · 2015</>),
    },
    {
      stat: "\u221223 à \u221234\u00A0%",
      label: "de chutes en moins avec l'exercice multimodal.",
      desc: (
        <>
          Une revue Cochrane 2019 (n&nbsp;=&nbsp;23&nbsp;407) démontre que l'exercice
          multimodal réduit le taux de chutes de 23 à 34&nbsp;% chez les personnes âgées
          vivant en communauté.
        </>
      ),
      ref: (<>Sherrington C. et al. · <em>Cochrane Database Syst Rev</em> · 2019</>),
    },
    {
      stat: "+50\u00A0%",
      label: "de probabilité de survie en plus quand on a une vie sociale active.",
      desc: (
        <>
          Selon la méta-analyse Holt-Lunstad (<em>PLoS Medicine</em>, 2010, 148 études,
          n&nbsp;≈&nbsp;309&nbsp;000), les personnes socialement bien entourées ont une
          probabilité de survie 50&nbsp;% supérieure. La communauté est un facteur de longévité.
        </>
      ),
      ref: (<>Holt-Lunstad J. et al. · <em>PLoS Medicine</em> 7(7) · 2010</>),
    },
    {
      stat: "14",
      label: "facteurs sur lesquels agir pour rester en forme cognitivement.",
      desc: (
        <>
          Selon le rapport 2024 de la Commission Lancet, 14 facteurs sont modifiables
          pour rester en forme cognitivement. L'activité physique et la vie sociale en font partie.
        </>
      ),
      ref: (<>Livingston G. et al. · <em>The Lancet</em> 404(10452) · 2024</>),
    },
  ];
  return (
    <section className="etudes-section" data-testid="etudes-section">
      <div className="container">
        <span className="eyebrow">Sources scientifiques</span>
        <h2 className="etudes-h2">
          Bouger et réfléchir <span className="accent">en même temps</span>.
        </h2>
        <p className="etudes-lead">
          <span className="etudes-lead-accent">L'un des leviers les mieux documentés</span>{" "}
          pour être au top — et le rester — après 50 ans. Une heure ou plus par semaine,
          à plusieurs, qui dérouille{" "}
          <span className="etudes-lead-accent">la tête autant que les jambes</span>. Et qui
          prévient pas mal de choses au passage. Dans la joie et la bonne humeur&nbsp;!
        </p>
        <p className="etudes-subtitle">Quatre études qui prouvent qu'on peut agir.</p>
        <div className="etudes-grid">
          {studies.map((s, i) => (
            <div className="etude-card" key={i}>
              <div className="etude-stat">{s.stat}</div>
              <div className="etude-label">{s.label}</div>
              <p className="etude-desc">{s.desc}</p>
              <div className="etude-ref">{s.ref}</div>
            </div>
          ))}
        </div>
        <p className="etudes-disclaimer">
          Ginger Circle s'inspire des principes validés par la recherche. La méthode
          elle-même n'a pas été testée cliniquement et ne se substitue à aucune
          prescription médicale.
        </p>
        <div className="section-cta-center">
          <button onClick={onJoin} className="cta-primary cta-big" data-testid="etudes-cta">
            Inscrivez-vous <span className="arrow">→</span>
          </button>
        </div>
      </div>
    </section>
  );
}

function Faq() {
  return (
    <section className="faq-section" data-testid="faq-section">
      <div className="container">
        <div className="faq-grid">
          <div>
            <span className="eyebrow">Une question&nbsp;?</span>
            <h3 className="faq-h3">Ginger Circle, qu'est-ce que c'est&nbsp;?</h3>
            <p className="faq-body">
              Une nouvelle méthode pour faire progresser son corps et son cerveau,
              avec des cours collectifs uniques&nbsp;!
            </p>
            <p className="faq-body">
              Nouvelle méthode en <em>«&nbsp;double-travail&nbsp;»</em>, adossée
              aux résultats des dernières recherches scientifiques.
            </p>
            <p className="faq-body faq-body--lead">
              Vous avez des questions&nbsp;? <em>Écrivez-nous&nbsp;!</em>
            </p>
            <div className="faq-cta-row">
              <a href="mailto:bonjour@gingercircle.fr" className="cta-primary" data-testid="faq-mailto">
                bonjour@gingercircle.fr <span className="arrow">→</span>
              </a>
              <a href="tel:+33662188624" className="cta-secondary" data-testid="faq-phone">
                <span aria-hidden="true">📞</span> 06 62 18 86 24
              </a>
            </div>
          </div>
          <div className="faq-photo">
            <img src={`${process.env.PUBLIC_URL}/saynete.png`} alt="Grand-père et petit-fils — scène de famille" />
            <p className="faq-caption">
              <em>Soyez prêts quand il se mettra à dire OUI&nbsp;!</em>
              <br />
              Oui au vélo, oui au tennis, oui aux échecs…
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer({ onJoin }) {
  return (
    <footer data-testid="site-footer">
      <div className="container">
        <div className="footer-grid">
          <div>
            <div className="footer-wordmark">Ginger<span className="dot">.</span></div>
          </div>
          <div className="footer-col">
            <h4>Ginger Circle</h4>
            <ul>
              <li><a href="#methode">La méthode</a></li>
              <li><a href="#formats">Nos formats</a></li>
              <li><a href="#etudes">La science</a></li>
            </ul>
          </div>
          <div className="footer-col">
            <h4>Rejoindre</h4>
            <ul>
              <li><button onClick={onJoin} data-testid="footer-inscription-btn">Inscrivez-vous</button></li>
            </ul>
          </div>
          <div className="footer-col">
            <h4>Contact</h4>
            <ul>
              <li><a href="tel:+33662188624" data-testid="footer-phone">06 62 18 86 24</a></li>
              <li><a href="mailto:bonjour@gingercircle.fr">bonjour@gingercircle.fr</a></li>
              <li><a href="https://gingercircle.fr">gingercircle.fr</a></li>
            </ul>
          </div>
          <div className="footer-col" data-testid="footer-socials">
            <h4>Suivez-nous</h4>
            <div className="footer-socials-row">
              <a
                href={SOCIAL_LINKS.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram Ginger Circle"
                className="social-icon social-icon-footer"
                data-testid="footer-social-instagram"
              >
                <Instagram size={20} strokeWidth={1.6} aria-hidden="true" />
              </a>
              <a
                href={SOCIAL_LINKS.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook Ginger Circle"
                className="social-icon social-icon-footer"
                data-testid="footer-social-facebook"
              >
                <Facebook size={20} strokeWidth={1.6} aria-hidden="true" />
              </a>
            </div>
          </div>
        </div>

        <div className="footer-bottom">
          <span>© Ginger Circle 2026</span>
          <span>
            <a href="#">Mentions légales</a>
            <a href="#">CGV</a>
            <a href="#">Politique de confidentialité</a>
          </span>
        </div>

        <p className="disclaimer">
          Ginger Circle s'inspire des principes validés par la recherche en activité physique
          adaptée et entraînement cognitif combiné. La méthode ne se substitue pas à une
          prescription médicale ni à un traitement. Les chiffres cités sur cette page renvoient à
          des études tierces dont les références complètes sont disponibles sur demande.
        </p>
      </div>
    </footer>
  );
}

function StickyCta({ onJoin }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      const hero = document.querySelector(".hero");
      if (!hero) return;
      const passed = window.scrollY > hero.offsetTop + hero.offsetHeight * 0.6;
      const atFooter = window.innerHeight + window.scrollY > document.body.offsetHeight - 280;
      setShow(passed && !atFooter);
    };
    document.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => document.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <button
      onClick={onJoin}
      className={`sticky-cta ${show ? "show" : ""}`}
      data-testid="sticky-cta"
    >
      Inscrivez-vous →
    </button>
  );
}

/* ─── Landing (V2 order) ─────────────────────────────────── */

const GOOGLE_FORM_URL =
  "https://docs.google.com/forms/d/e/1FAIpQLSec6Xkm2BYnORdIj8iY7Lpb9Gc702WWZLfovl7S2TVn-zWaIg/viewform?usp=dialog";

function Landing() {
  const openForm = () => {
    window.open(GOOGLE_FORM_URL, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="App">
      <Toaster position="top-center" richColors />
      <Nav onJoin={openForm} />
      <SharpLaunch onJoin={openForm} />
      <Places />
      <Verbatims onJoin={openForm} />
      <PressNouvelleRepublique articleUrl={null} />
      <ApprocheUnique />
      <Tarifs onJoin={openForm} />
      <Methode onJoin={openForm} />
      <Etudes onJoin={openForm} />
      <Faq />
      <Footer onJoin={openForm} />
      <StickyCta onJoin={openForm} />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/admin-ginger" element={<Admin />} />
        <Route path="*" element={<Landing />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
